import React from 'react';

import { gql, useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Container, FormControl, FormHelperText, FormLabel, Grid, TextField } from '@mui/material';
import { get, isNil } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { BottomSafeArea, LoadingButton } from '../../../components';
import { AppContext } from '../../../contexts/app_context';
import { createScopedI18n, i18n } from '../../../i18n/i18n';
import { errorsPairI18n } from '../../../utils/libs';

const changeEmailFieldI18n = createScopedI18n('graphql.fields.change_email');
const changeEmailErrorI18n = createScopedI18n('graphql.errors.types.change_email.fields', { joinOutput: true });

const changeEmailGql = gql`
  mutation changeEmail($email: String!, $password: String!) {
    changeEmail(email: $email, password: $password) {
      success
      errors
      data {
        id
        email
      }
    }
  }
`;

const changeEmailSchema = Yup.object({
  email: Yup.string().email(changeEmailErrorI18n('email.invalid')).required(changeEmailErrorI18n('email.blank')),
  password: Yup.string().required(changeEmailErrorI18n('password.blank')),
});

type ChangeEmailValueType = Yup.InferType<typeof changeEmailSchema>;

const UserEditPage = () => {
  const { contextUser, setContextUser } = React.useContext(AppContext);

  const { control, handleSubmit, reset, formState } = useForm<ChangeEmailValueType>({
    defaultValues: {
      email: contextUser?.email ?? undefined,
    },
    resolver: yupResolver(changeEmailSchema),
  });

  const emailDirty = formState.dirtyFields.email;

  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [changeEmailMutation, { loading: changeEmailLoading }] = useMutation(changeEmailGql);

  const submitHandler = async (values: ChangeEmailValueType) => {
    setErrorMessage(null);

    try {
      const { data } = await changeEmailMutation({
        variables: {
          email: values.email,
          password: values.password,
        },
      });

      if (data?.changeEmail?.success) {
        const newEmail = data.changeEmail.data.email ?? values.email;
        setContextUser({ ...contextUser, email: newEmail } as typeof contextUser);
        reset({ email: newEmail, password: '' });
      } else {
        const errors = errorsPairI18n(changeEmailErrorI18n, data?.changeEmail?.errors ?? null);
        setErrorMessage(errors ?? i18n.t('general.error'));
      }
    } catch (error) {
      setErrorMessage(get(error, 'message', i18n.t('general.error')));
    }
  };

  return (
    <Container maxWidth="sm" sx={{ paddingY: 2 }}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <Grid container gap={2}>
          <Controller
            key="email"
            name="email"
            control={control}
            render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
              <FormControl id={field.name} fullWidth error={invalid}>
                <Grid item container alignItems="center" gap={1}>
                  <Grid item xs={3}>
                    <FormLabel>{changeEmailFieldI18n('email')} *</FormLabel>
                  </Grid>
                  <Grid item xs>
                    <TextField id={field.name} type="text" fullWidth value={value ?? ''} error={invalid} {...field} />
                  </Grid>
                </Grid>
                <Grid item container gap={1}>
                  <Grid item xs={3} />
                  <Grid item xs>
                    <FormHelperText>{error?.message}</FormHelperText>
                  </Grid>
                </Grid>
              </FormControl>
            )}
          />

          <Controller
            key="password"
            name="password"
            control={control}
            render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
              <FormControl id={field.name} fullWidth error={invalid}>
                <Grid item container alignItems="center" gap={1}>
                  <Grid item xs={3}>
                    <FormLabel>{changeEmailFieldI18n('password')} *</FormLabel>
                  </Grid>
                  <Grid item xs>
                    <TextField
                      id={field.name}
                      type="password"
                      fullWidth
                      autoComplete="current-password"
                      value={value ?? ''}
                      error={invalid}
                      {...field}
                    />
                  </Grid>
                </Grid>
                <Grid item container gap={1}>
                  <Grid item xs={3} />
                  <Grid item xs>
                    <FormHelperText>{error?.message}</FormHelperText>
                  </Grid>
                </Grid>
              </FormControl>
            )}
          />

          {isNil(errorMessage) ? null : <Alert severity="error">{errorMessage}</Alert>}

          <LoadingButton
            type="submit"
            disabled={changeEmailLoading || !emailDirty}
            loading={changeEmailLoading}
            fullWidth
            variant="contained"
          >
            {i18n.t('general.confirm')}
          </LoadingButton>
        </Grid>
      </form>

      <BottomSafeArea />
    </Container>
  );
};

export default UserEditPage;
